import {
  ArrowLeftOutlined,
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import {
  Button,
  Flex,
  Input,
  Popconfirm,
  Space,
  Table,
  TableColumnType,
} from "antd";
import { debounce } from "lodash";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ModalAddStationeryCategory } from "../../components/modal/hrm/ModalAddStationeryCategory";
import { IQuery, IStationeryCategory } from "../../models/Hrm";
import {
  useDeleteStationeryCategory,
  useGetCategories,
} from "../../query/HrmQuery";

const HrmStationeryCategory = () => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingCategory, setEditingCategory] =
    useState<IStationeryCategory | null>(null);
  const navigate = useNavigate();

  const [filters, setFilters] = useState<IQuery>({
    page: 1,
    limit: 10,
  });

  const { data, isLoading } = useGetCategories(filters);
  const deleteMutation = useDeleteStationeryCategory();

  const debouncedSearch = React.useMemo(
    () =>
      debounce((field: string, value: string) => {
        setFilters((prev) => ({
          ...prev,
          [field]: value || undefined,
          page: 1,
        }));
      }, 300),
    [],
  );

  const showModal = (category: IStationeryCategory | null = null) => {
    setEditingCategory(category);
    setIsModalVisible(true);
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    setEditingCategory(null);
  };

  const handleDelete = (id: number) => {
    deleteMutation.mutate(id);
  };

  const columns: TableColumnType<IStationeryCategory>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
    },
    {
      title: "Tên danh mục",
      dataIndex: "name",
      key: "name",
      filterDropdown: () => (
        <Input
          placeholder="Tìm theo tên"
          onChange={(e) => debouncedSearch("name", e.target.value)}
          prefix={<SearchOutlined />}
          style={{ padding: 8 }}
        />
      ),
    },
    {
      title: "Thao tác",
      key: "action",
      align: "center" as const,
      width: 120,
      render: (_: any, record: IStationeryCategory) => (
        <Space>
          <Button icon={<EditOutlined />} onClick={() => showModal(record)} />
          <Popconfirm
            title="Bạn có chắc muốn xóa?"
            onConfirm={() => handleDelete(record.id)}
            okText="Xóa"
            cancelText="Hủy"
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <Flex className="p-4" vertical gap={10}>
      <Flex align="center" justify="space-between" gap="small" wrap="wrap">
        <Button
          type="default"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
        >
          Quay lại
        </Button>
        <Space wrap>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => showModal()}
          >
            Thêm mới
          </Button>
        </Space>
      </Flex>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={data?.data}
        loading={isLoading}
        pagination={{
          current: data?.pagination?.page,
          pageSize: data?.pagination?.limit,
          total: data?.pagination?.total,
          onChange: (page, pageSize) =>
            setFilters((prev) => ({ ...prev, page, limit: pageSize })),
        }}
      />

      <ModalAddStationeryCategory
        isOpen={isModalVisible}
        onCancel={handleCancel}
        editingCategory={editingCategory}
      />
    </Flex>
  );
};

export default HrmStationeryCategory;
